import type { PropsWithChildren } from 'react';
import React, { useCallback, useState } from 'react';
import type { GoogleMapProps } from '@react-google-maps/api';
import { GoogleMap as Map, useJsApiLoader, TrafficLayer, BicyclingLayer, TransitLayer } from '@react-google-maps/api';

import Loader from 'src/components/ui/Loader';
import { defaultZoom, francePosition, googleApiLoaderParams } from 'src/constants/googleMap';
import useGooglePOIFilters from 'src/hooks/useGooglePOIFilters';
import GoogleMapPOIFilters from 'src/components/map/GoogleMapPOIFilters';
import GoogleMapControl from 'src/components/map/GoogleMapControl';
import GoogleMapTypeControl from 'src/components/map/GoogleMapTypeControl';

const containerStyle = {
    width: '100%',
    height: '100%',
};

export enum ControlPosition {
    BOTTOM_CENTER = 11,
    BOTTOM_LEFT = 10,
    BOTTOM_RIGHT = 12,
    LEFT_BOTTOM = 6,
    LEFT_CENTER = 4,
    LEFT_TOP = 5,
    RIGHT_BOTTOM = 9,
    RIGHT_CENTER = 8,
    RIGHT_TOP = 7,
    TOP_CENTER = 2,
    TOP_LEFT = 1,
    TOP_RIGHT = 3,
}

export enum MapTypeId {
    /** This map type displays a transparent layer of major streets on satellite images. */
    HYBRID = 'hybrid',
    /** This map type displays a normal street map. */
    ROADMAP = 'roadmap',
    /** This map type displays satellite images. */
    SATELLITE = 'satellite',
    /** This map type displays maps with physical features such as terrain and vegetation. */
    TERRAIN = 'terrain',
}

export const googleMapDefaultOptions: google.maps.MapOptions = {
    zoomControl: true,
    zoomControlOptions: {
        position: ControlPosition.RIGHT_BOTTOM, // default one
    },
    streetViewControl: true,
    streetViewControlOptions: {
        position: ControlPosition.RIGHT_BOTTOM, // default one
    },
    fullscreenControl: true,
    // WARNING : if you want to use another position you will probably be facing this issue => https://github.com/JustFly1984/react-google-maps-api/issues/2159
    // fullscreenControlOptions: {
    //     position: ControlPosition.TOP_RIGHT, // default one
    // },
    mapTypeControl: true, // default mapTypeControl but if we want to improve map type control buttons we can use mapTypeId like bellow to select map type
    mapTypeControlOptions: {
        position: ControlPosition.LEFT_BOTTOM,
    },
    rotateControl: true, // button available only in satellite view, this allows to play with tilt and rotation
    rotateControlOptions: {
        position: ControlPosition.RIGHT_BOTTOM, // above the street view
    },
};

export type GoogleMapComponentProps = {
    zoom?: number;
    center?: google.maps.LatLngLiteral;
    options?: google.maps.MapOptions;
    setMap?: (map: google.maps.Map | null) => void;
    shouldDisplayPOIFilters?: boolean;
    onFullscreenChange?: (isFullscreen: boolean) => void;
    onTilesLoaded?: () => void;
} & GoogleMapProps;

const GoogleMap: React.FC<PropsWithChildren<GoogleMapComponentProps>> = ({
    center = francePosition.point,
    zoom = defaultZoom,
    options = googleMapDefaultOptions,
    children,
    setMap: setMapForParent,
    shouldDisplayPOIFilters = false,
    onFullscreenChange,
    ...rest
}) => {
    const { isLoaded } = useJsApiLoader(googleApiLoaderParams);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [mapTypeId, setMapTypeId] = useState<MapTypeId>((options.mapTypeId as MapTypeId | null) ?? MapTypeId.ROADMAP);
    const [addTrafficLayer, setAddTrafficLayer] = useState<boolean>(false);
    const [addBicyclingLayer, setAddBicyclingLayer] = useState<boolean>(false);
    const [addTransitLayer, setAddTransitLayer] = useState<boolean>(false);
    const [streetViewIsOpen, setStreetViewIsOpen] = useState<boolean>(false);

    const googlePOIFiltersValues = useGooglePOIFilters();

    const onMapLoad = useCallback(
        (mapLoaded: google.maps.Map) => {
            if (setMapForParent) {
                setMapForParent(mapLoaded);
            }
            setMap(mapLoaded);
        },
        [setMapForParent],
    );

    const onUnmount = useCallback(
        (mapLoaded: google.maps.Map | null) => {
            if (setMapForParent) {
                setMapForParent(null);
            }
            setMap(null);
        },
        [setMapForParent],
    );

    const onBoundsChanged = () => {
        if (map) {
            const mapDivChild = map.getDiv().children[0];
            onFullscreenChange?.(
                mapDivChild.clientWidth === window.innerWidth && mapDivChild.clientHeight === window.innerHeight,
            );
        }
    };

    const streetView = map?.getStreetView();
    streetView?.addListener('visible_changed', () => {
        setStreetViewIsOpen(streetView.getVisible());
    });

    return isLoaded ? (
        <>
            {shouldDisplayPOIFilters && !streetViewIsOpen && map && (
                <GoogleMapControl
                    containerId="POIFiltersContainer"
                    position={ControlPosition.TOP_LEFT}
                    zIndex={20}
                    map={map}
                    width="100%"
                >
                    <GoogleMapPOIFilters
                        {...googlePOIFiltersValues}
                        fullscreenControl={Boolean(options.fullscreenControl)}
                        mapTypeControl={Boolean(options.mapTypeControl)}
                    />
                </GoogleMapControl>
            )}
            <Map
                data-target-id="google-map"
                mapContainerStyle={containerStyle}
                center={center}
                zoom={zoom}
                onLoad={onMapLoad}
                onUnmount={onUnmount}
                onBoundsChanged={onFullscreenChange ? onBoundsChanged : undefined}
                options={{
                    ...options,
                    minZoom: 2,
                    mapTypeControl: false, // we use a custom control, see bellow
                    mapTypeControlOptions: undefined,
                    mapTypeId,
                    styles: shouldDisplayPOIFilters && !options.mapId ? googlePOIFiltersValues.styles : options.styles,
                }}
                {...rest}
            >
                {/* Child components, such as markers, info windows, etc. */}
                {children}
                {options.mapTypeControl && addTrafficLayer && <TrafficLayer />}
                {options.mapTypeControl && addBicyclingLayer && <BicyclingLayer />}
                {options.mapTypeControl && addTransitLayer && <TransitLayer />}
            </Map>
            {options.mapTypeControl && map && (
                <GoogleMapControl
                    containerId="containerId"
                    position={(options.mapTypeControlOptions?.position ?? undefined) as ControlPosition}
                    zIndex={20}
                    map={map}
                    width="fit-content"
                >
                    <GoogleMapTypeControl
                        setMapTypeId={setMapTypeId}
                        mapTypeId={mapTypeId}
                        setAddTrafficLayer={setAddTrafficLayer}
                        addTrafficLayer={addTrafficLayer}
                        setAddBicyclingLayer={setAddBicyclingLayer}
                        addBicyclingLayer={addBicyclingLayer}
                        setAddTransitLayer={setAddTransitLayer}
                        addTransitLayer={addTransitLayer}
                    />
                </GoogleMapControl>
            )}
        </>
    ) : (
        <Loader />
    );
};

export default GoogleMap;
